import React, { useEffect, useRef, useState } from 'react';
import { getCar } from '../../../api/carsApi';
import { formatPhoneBr } from '../../../utils/phoneMask';
import { useEsteiras } from '../../../hooks/useEsteiras';
import ReleaseStatusInfo from '../../release-status-info/ReleaseStatusInfo';
import ObservacoesAtendimento from '../../observacoes-atendimento/ObservacoesAtendimento';
import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import ComentariosDoVeiculo from '../../comentarios/ComentariosDoVeiculo';
import TagsDaEsteira from '../../tags-da-esteira/TagsDaEsteira';
import SeloPosEvento from '../../selo-pos-evento/SeloPosEvento';
import SeloPrazoDaEtapa from '../../selo-prazo/SeloPrazoDaEtapa';
import SeloEsteiraParalela from '../../selo-esteira-paralela/SeloEsteiraParalela';
import ControleParalelasModal from '../controle-paralelas/ControleParalelasModal';
import BlocoRetornoDeGarantia from '../../selo-pos-evento/BlocoRetornoDeGarantia';

import Pen from '../../../img/pen.svg';
import ChangeStatus from '../../../img/change-status.svg';
import EyeIcon from '../../../img/eye.svg';
import SendOrange from '../../../img/sendOrange.svg';
import Entry from '../../../img/entry.svg';
import Exit from '../../../img/exit.svg';

import './RecepcaoActionsModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * at-65 (BUG-V73): `onViewDetails` e `onApprovePendingStatus` foram REMOVIDAS.
 *
 * As duas eram declaradas, recebidas e **nunca chamadas** no corpo deste modal — o
 * `CarDetailsModal` que a Recepção montava era, por consequência, inalcançável, e a
 * aprovação de esteira só acontece pelo botão da própria linha da tabela (que segue
 * funcionando). Removidas em vez de comentadas, conforme a regra de 02/08/2026.
 *
 * ⚠️ Se a Recepção **deveria** ter uma ação "Ver detalhes" neste modal, isso é decisão de
 * produto (feature ausente), não conserto de código morto — registrado no backlog.
 */
interface RecepcaoActionsModalProps {
  isOpen: boolean;
  carId: string | null;
  onCancel: () => void;
  onEditCar: (carId: string) => void;
  onChangeStatus: (carId: string) => void;
  onViewChecklist: (carId: string, type: 'ENTRADA' | 'SAIDA') => void;
  /**
   * at-96: `ehPosEvento` acompanha a ação porque o tipo do checklist é DERIVADO do
   * atendimento — quem sabe disso é este modal, que já carregou o veículo.
   */
  onStartChecklist: (carId: string, type: 'ENTRADA' | 'SAIDA', ehPosEvento: boolean) => void;
  onStartChecklistSaida: (carId: string, ehPosEvento: boolean) => void;
  hasEntradaCompleted: boolean;
  hasSaidaCompleted: boolean;
  /** Atendimento encerrado (ENTREGA): só ações de visualização/compartilhamento, sem edição. */
  deliveredMode?: boolean;
  /**
   * Envia a pesquisa de satisfação ao associado. O botão só aparece quando a
   * função é passada — e é assim de propósito: `deliveredMode` também é ligado
   * para o TI em leitura (recepcao.tsx), que NÃO tem a rota (ADR 0012). Pendurar
   * a ação nele daria ao TI um botão que só devolve 403. Quem decide é o chamador.
   */
  onEnviarPesquisa?: (carId: string) => void;
  /** Rótulo do botão enquanto o link é gerado. */
  enviandoPesquisa?: boolean;
  /**
   * at-134: a situação da pesquisa deste atendimento ("Enviada em 20/10/2026"), exibida junto do
   * botão. Vem da linha da aba Entregues — sem chamada nova.
   */
  situacaoDaPesquisa?: string;
  /**
   * at-126: abre o WhatsApp com o link da página de acompanhamento do associado. Opcional pelo
   * mesmo motivo de `onEnviarPesquisa` — o TI em leitura não tem a rota (ADR 0012). Vale nos dois
   * modos: o acesso segue aberto por 30 dias depois da entrega.
   */
  onCompartilharAcompanhamento?: (carId: string) => void;
  compartilhandoAcompanhamento?: boolean;
  /**
   * Edição do veículo em atendimento ENTREGUE (decisão do usuário, 18/08/2026):
   * o totem manda o associado "procurar a recepção" quando o telefone não
   * confere, e a recepção precisa de onde corrigir o cadastro. Callback próprio,
   * pelo mesmo motivo de `onEnviarPesquisa`: `deliveredMode` também é o TI em
   * leitura, que não tem a rota de edição.
   */
  onEditarEntregue?: (carId: string) => void;
  /**
   * at-89: abre a edição do motivo da pendência da esteira atual. Prop OPCIONAL pelo mesmo
   * motivo das duas acima — `deliveredMode` também vale para o TI em leitura, que não deve
   * receber botão de escrita (ADR 0012). Quem decide é o chamador.
   */
  onEditarMotivo?: (carId: string) => void;
  /**
   * Trava o fechamento por "clique fora" enquanto um modal filho está aberto
   * sobre este (ex: o cadastro rápido de contato do proprietário).
   *
   * Necessário porque o filho é renderizado FORA do `ref` deste modal: sem a
   * trava, qualquer clique dentro dele conta como clique externo e derruba a
   * tela de trás. Mesma correção que o `ChecklistModal` faz para o drawer
   * "Como Funciona".
   */
  travarCliqueFora?: boolean;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const RecepcaoActionsModal: React.FC<RecepcaoActionsModalProps> = ({
  isOpen,
  carId,
  onCancel,
  onEditCar,
  onChangeStatus,
  onViewChecklist,
  onStartChecklist,
  onStartChecklistSaida,
  hasEntradaCompleted,
  hasSaidaCompleted,
  deliveredMode = false,
  onEnviarPesquisa,
  enviandoPesquisa = false,
  situacaoDaPesquisa,
  onCompartilharAcompanhamento,
  compartilhandoAcompanhamento = false,
  onEditarEntregue,
  onEditarMotivo,
  travarCliqueFora = false,
  sidebarRef,
}) => {
  const { labelDe } = useEsteiras(true);
  const modalRef = useRef<HTMLDivElement>(null);
  const [car, setCar] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // at-89: incrementado pelo chamador ao salvar o motivo, para o bloco recarregar sem F5.
  const [motivoRefresh] = useState(0);
  // at-99: o selo ao lado da placa abre o controle das paralelas (pedido do Victor, 10/09/2026).
  const [controleParalelasAberto, setControleParalelasAberto] = useState(false);

  const fetchCarData = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const carData = await getCar(id);
      setCar(carData);
    } catch (err) {
      setError('Erro ao carregar os dados do veículo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && carId) {
      fetchCarData(carId);
    }
  }, [isOpen, carId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Modal filho aberto sobre este: o clique dentro dele é "fora" deste `ref`,
      // e sem a guarda derrubaria a tela de trás no primeiro clique.
      if (travarCliqueFora || controleParalelasAberto) return;

      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef, travarCliqueFora, controleParalelasAberto]);

  if (!isOpen) return null;

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    });
  };

  return (
    <div className="add-car-modal-overlay">
      <div className="recepcao-actions-modal-content" ref={modalRef}>
        
        {/* Modal Header */}
        <div className="recepcao-actions-modal-header">
          <div className="header-info">
            <h2>
              {car?.plate || 'Carregando...'}
              {/* at-96: retorno de garantia, ao lado da placa — na mesma linha (regra do Victor). */}
              <SeloPosEvento posEvento={car?.posEvento} />
              <SeloPrazoDaEtapa car={car} />
              {/* at-99: a Recepção precisa saber que há outro setor trabalhando no veículo — é
                  o que ela responde quando o associado liga perguntando do carro. */}
              <SeloEsteiraParalela
                esteirasParalelas={car?.esteirasParalelas}
                onClick={() => setControleParalelasAberto(true)}
              />
              {/* at-89: motivo da pendência ao lado da placa (Victor, 03/09/2026). */}
              {car?.id && (
                <TagsDaEsteira
                  carId={car.id}
                  esteiraAtual={car.status}
                  variante="inline"
                  refreshKey={motivoRefresh}
                />
              )}
            </h2>
            {car?.model && <span className="vehicle-model">{car.model}</span>}
          </div>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        {loading ? (
          <div className="recepcao-modal-loading">Carregando dados do veículo...</div>
        ) : error ? (
          <div className="recepcao-modal-error">{error}</div>
        ) : car ? (
          <div className="recepcao-actions-modal-body">
            
            {/* Left Section: Details & Images */}
            <div className="recepcao-details-left">
              
              {/* at-96: o serviço que este atendimento está retomando. */}
              <BlocoRetornoDeGarantia posEvento={car.posEvento} />

              {/* Info Grid */}
              <div className="info-section">
                <h3>Informações do Veículo</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nº do Evento</span>
                    <span className="info-value">{car.eventNumber || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Chassi</span>
                    <span className="info-value">{car.chassis || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Classe</span>
                    <span className="info-value">{car.carClass || '-'}</span>
                  </div>
                  {/*
                    Proprietário e telefone ficam à vista porque o telefone é a chave
                    de acesso do link público (checklist e pesquisa de satisfação):
                    sem ele o compartilhamento não sai, e antes só o erro do envio
                    revelava a falta.
                  */}
                  <div className="info-item">
                    <span className="info-label">Proprietário</span>
                    <span className="info-value">{car.ownerName || 'Não informado'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Telefone</span>
                    <span className="info-value">
                      {car.ownerPhone ? formatPhoneBr(car.ownerPhone) : 'Não informado'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tipo de Dano</span>
                    <span className="info-value">{car.damageType || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Cor</span>
                    <span className="info-value">{car.color || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Data de Entrada</span>
                    <span className="info-value">{formatDate(car.entryDate)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Dias na Oficina</span>
                    <span className="info-value">{car.daysInShop ?? '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Esteira Atual</span>
                    <span className="info-value status-badge">{car.status ? labelDe(car.status) : 'Fazer Checklist'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Esteira Pendente</span>
                    <span className="info-value pending-badge">{labelDe(car.pendingStatus)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Colaboradores</span>
                    <span className="info-value">{car.responsibleName || car.responsible?.name || '-'}</span>
                  </div>
                  <ReleaseStatusInfo car={car} />
                </div>

                {/* at-54: texto livre do atendimento (componente compartilhado; some quando vazio) */}
                <ObservacoesAtendimento
                  observacoesCliente={car.observacoesCliente}
                  observacoesOficina={car.observacoesOficina}
                />

                {/* at-89: é aqui que a Recepção responde "o que já fizeram no meu carro?" */}
                <ProgressoSubtarefas carId={car.id} />
                <ComentariosDoVeiculo carId={car.id} />
              </div>

            </div>

            {/* Right Section: Actions Sidebar */}
            <div className="recepcao-actions-right">
              <h3>{deliveredMode ? 'Ações do Atendimento' : 'Ações de Recepção'}</h3>
              
              <div className="actions-list">

                {!deliveredMode && (
                  <>
                    <button
                      type="button"
                      className="action-button-item"
                      onClick={() => onEditCar(car.id)}
                    >
                      <img src={Pen} alt="" className="action-icon" />
                      <span>Editar Carro</span>
                    </button>

                    <button
                      type="button"
                      className="action-button-item"
                      onClick={() => onChangeStatus(car.id)}
                    >
                      <img src={ChangeStatus} alt="" className="action-icon" />
                      <span>Solicitar Mudança de Esteira</span>
                    </button>

                    {/* at-89: ação própria para o motivo da pendência. Antes só existia dentro
                        do "Editar Carro", e por isso passava batido. */}
                    {onEditarMotivo && (
                      <button
                        type="button"
                        className="action-button-item"
                        onClick={() => onEditarMotivo(car.id)}
                      >
                        <img src={ChangeStatus} alt="" className="action-icon" />
                        <span>Atualizar Motivo da Pendência</span>
                      </button>
                    )}
                  </>
                )}

                {hasEntradaCompleted ? (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onViewChecklist(car.id, 'ENTRADA')}
                  >
                    <img src={EyeIcon} alt="" className="action-icon" />
                    <span>Visualizar Checklist de Entrada</span>
                  </button>
                ) : !deliveredMode ? (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onStartChecklist(car.id, 'ENTRADA', Boolean(car.posEvento))}
                  >
                    <img src={Entry} alt="" className="action-icon" />
                    <span>Fazer Checklist de Entrada</span>
                  </button>
                ) : null}

                {hasSaidaCompleted ? (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onViewChecklist(car.id, 'SAIDA')}
                  >
                    <img src={EyeIcon} alt="" className="action-icon" />
                    <span>Visualizar Checklist de Saída</span>
                  </button>
                ) : !deliveredMode ? (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onStartChecklistSaida(car.id, Boolean(car.posEvento))}
                  >
                    <img src={Exit} alt="" className="action-icon" />
                    <span>Fazer Checklist de Saída</span>
                  </button>
                ) : null}

                {deliveredMode && onEditarEntregue && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onEditarEntregue(car.id)}
                  >
                    <img src={Pen} alt="" className="action-icon" />
                    <span>Editar Carro</span>
                  </button>
                )}

                {onCompartilharAcompanhamento && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onCompartilharAcompanhamento(car.id)}
                    disabled={compartilhandoAcompanhamento}
                  >
                    <img src={SendOrange} alt="" className="action-icon" />
                    <span>{compartilhandoAcompanhamento ? 'Gerando link...' : 'Compartilhar Acompanhamento'}</span>
                  </button>
                )}

                {onEnviarPesquisa && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onEnviarPesquisa(car.id)}
                    disabled={enviandoPesquisa}
                  >
                    {/* O olho é "visualizar"; esta ação ENVIA a pesquisa ao associado. */}
                    <img src={SendOrange} alt="" className="action-icon" />
                    <span>{enviandoPesquisa ? 'Gerando link...' : 'Enviar Pesquisa de Satisfação'}</span>
                  </button>
                )}
                {onEnviarPesquisa && situacaoDaPesquisa && (
                  <span style={{ fontSize: 12, color: 'var(--tema-texto-3, #64748b)', paddingLeft: 4 }}>
                    Pesquisa: {situacaoDaPesquisa}
                  </span>
                )}

                {deliveredMode && !hasEntradaCompleted && !hasSaidaCompleted && (
                  <div className="no-images-box">
                    Este atendimento não possui checklist registrado.
                  </div>
                )}

              </div>
            </div>

          </div>
        ) : (
          <div className="recepcao-modal-error">Nenhum dado encontrado.</div>
        )}

      </div>

      {/* at-99: controle das esteiras em paralelo, aberto pelo selo. ⚠️ Está na guarda de "clicou
          fora" deste modal — sem isso o primeiro clique aqui dentro fecharia os dois. */}
      {controleParalelasAberto && car && (
        <ControleParalelasModal
          carId={car.id}
          plate={car.plate}
          esteiraPrincipalId={car.esteira?.id ?? car.esteiraId}
          esteiraPrincipalNome={car.esteira?.nome ?? labelDe(car.status)}
          esteirasParalelas={car.esteirasParalelas}
          onCancel={() => setControleParalelasAberto(false)}
          onAlterado={() => void fetchCarData(car.id)}
          sidebarRef={sidebarRef}
        />
      )}
    </div>
  );
};

export default RecepcaoActionsModal;
